import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Chip, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Select, FormControl, InputLabel, Grid, IconButton,
  Tooltip, Card, CardContent
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  BugReport as BugIcon,
  CheckCircle as ResolvedIcon,
  HourglassEmpty as OpenIcon,
  Search as InvestigatingIcon,
  Cancel as RejectedIcon,
  TrendingUp as TrendIcon
} from '@mui/icons-material';
import api from '../../services/api';

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'warning', icon: <OpenIcon fontSize="small" /> },
  investigating: { label: 'Investigating', color: 'info', icon: <InvestigatingIcon fontSize="small" /> },
  resolved: { label: 'Resolved', color: 'success', icon: <ResolvedIcon fontSize="small" /> },
  rejected: { label: 'Rejected', color: 'default', icon: <RejectedIcon fontSize="small" /> }
};

const TYPE_LABELS = {
  wrong_answer: 'Wrong Answer',
  unclear_question: 'Unclear Question',
  formatting_error: 'Formatting Error',
  inappropriate_content: 'Inappropriate Content',
  other: 'Other'
};

export default function ContentReportsTab({ schoolId }) {
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Detail dialog
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [updateStatus, setUpdateStatus] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/api/content-reports/admin/stats');
      setStats(res.data.data || res.data);
    } catch (err) {
      console.error('Failed to load report stats:', err);
    }
  }, []);

  const fetchReports = useCallback(async () => {
    try {
      setListLoading(true);
      const params = { page, limit: 15 };
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.reportType = typeFilter;
      if (priorityFilter) params.priority = priorityFilter;
      const res = await api.get('/api/content-reports/admin', { params });
      const data = res.data.data || res.data;
      setReports(data.reports || data || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      setError('Failed to load reports');
    } finally {
      setListLoading(false);
    }
  }, [page, statusFilter, typeFilter, priorityFilter]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchReports()]);
      setLoading(false);
    };
    init();
  }, [fetchStats, fetchReports]);

  const handleFilterChange = () => {
    setPage(1);
    fetchReports();
  };

  const openDetail = async (reportId) => {
    try {
      setDetailLoading(true);
      const res = await api.get(`/api/content-reports/admin/${reportId}`);
      const report = (res.data.data && res.data.data.report) || res.data.data || res.data.report || res.data;
      setSelectedReport(report);
      setUpdateStatus(report.status || 'open');
      setResolutionNotes(report.resolutionNotes || '');
    } catch (err) {
      setError('Failed to load report detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleUpdateReport = async () => {
    if (!selectedReport) return;
    try {
      setSaving(true);
      await api.patch(`/api/content-reports/admin/${selectedReport._id}`, {
        status: updateStatus,
        resolutionNotes
      });
      setSuccess('Report updated successfully');
      setSelectedReport(null);
      fetchStats();
      fetchReports();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update report');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {(() => {
            const bs = (stats.byStatus && typeof stats.byStatus === 'object' && !Array.isArray(stats.byStatus))
              ? stats.byStatus : {};
            const totalReports = Object.values(bs).reduce((sum, v) => sum + (v || 0), 0);
            return [
              {
                label: 'Open',
                value: bs.open || 0,
                color: '#ed6c02'
              },
              {
                label: 'Investigating',
                value: bs.investigating || 0,
                color: '#0288d1'
              },
              {
                label: 'Resolved',
                value: bs.resolved || 0,
                color: '#2e7d32'
              },
              {
                label: 'Total Reports',
                value: totalReports,
                color: '#9c27b0'
              }
            ];
          })().map((stat, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="h4" fontWeight="bold" sx={{ color: stat.color }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Report Type Breakdown */}
      {Array.isArray(stats?.byType) && stats.byType.length > 0 && (
        <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1, alignSelf: 'center' }}>
            By type:
          </Typography>
          {stats.byType.map((t, i) => (
            <Chip
              key={i}
              label={`${TYPE_LABELS[t._id] || t._id}: ${t.count}`}
              size="small"
              variant="outlined"
            />
          ))}
        </Box>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ mr: 1 }}>Filters:</Typography>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="investigating">Investigating</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Report Type</InputLabel>
            <Select
              value={typeFilter}
              label="Report Type"
              onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="wrong_answer">Wrong Answer</MenuItem>
              <MenuItem value="unclear_question">Unclear Question</MenuItem>
              <MenuItem value="formatting_error">Formatting Error</MenuItem>
              <MenuItem value="inappropriate_content">Inappropriate</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priorityFilter}
              label="Priority"
              onChange={e => { setPriorityFilter(e.target.value); setPage(1); }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="school">School</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => { fetchStats(); fetchReports(); }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Reports Table */}
      <TableContainer component={Paper}>
        {listLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : reports.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <BugIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">No Content Reports</Typography>
            <Typography variant="body2" color="text.secondary">
              No reports have been submitted yet. Reports from learners about content errors will appear here.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Content</TableCell>
                <TableCell>Reporter</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map(report => {
                const sc = STATUS_CONFIG[report.status] || STATUS_CONFIG.open;
                const snapshot = report.contentSnapshot || {};
                return (
                  <TableRow key={report._id} hover>
                    <TableCell>
                      <Chip
                        label={TYPE_LABELS[report.reportType] || report.reportType}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {snapshot.topic
                          ? `G${snapshot.grade} ${snapshot.topic}`
                          : report.description?.slice(0, 40) || 'No description'}
                      </Typography>
                      {snapshot.subtopic && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {snapshot.subtopic}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {report.reporter?.username || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={report.priority || 'normal'}
                        size="small"
                        color={report.priority === 'school' ? 'error' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={sc.icon}
                        label={sc.label}
                        size="small"
                        color={sc.color}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(report.createdAt).toLocaleDateString('en-ZA')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => openDetail(report._id)}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
          <Button
            size="small"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </Button>
          <Typography variant="body2" sx={{ alignSelf: 'center' }}>
            Page {page} of {totalPages}
          </Typography>
          <Button
            size="small"
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </Button>
        </Box>
      )}

      {/* Report Detail Dialog */}
      <Dialog
        open={!!selectedReport}
        onClose={() => setSelectedReport(null)}
        maxWidth="sm"
        fullWidth
      >
        {detailLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : selectedReport && (
          <>
            <DialogTitle>
              Report Detail — {TYPE_LABELS[selectedReport.reportType] || selectedReport.reportType}
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Content Info */}
                {selectedReport.contentSnapshot && (
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="subtitle2" gutterBottom>Content</Typography>
                    <Typography variant="body2">
                      Grade {selectedReport.contentSnapshot.grade} •{' '}
                      {selectedReport.contentSnapshot.subject} •{' '}
                      {selectedReport.contentSnapshot.topic}
                    </Typography>
                    {selectedReport.contentSnapshot.subtopic && (
                      <Typography variant="caption" color="text.secondary">
                        Subtopic: {selectedReport.contentSnapshot.subtopic}
                      </Typography>
                    )}
                  </Paper>
                )}

                {/* Description */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Description</Typography>
                  <Typography variant="body2">
                    {selectedReport.description || 'No description provided'}
                  </Typography>
                </Box>

                {/* Reporter */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Reporter</Typography>
                  <Typography variant="body2">
                    {selectedReport.reporter?.username || 'Unknown'} •{' '}
                    {new Date(selectedReport.createdAt).toLocaleString('en-ZA')}
                  </Typography>
                </Box>

                {/* Current Status */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography variant="subtitle2">Current Status:</Typography>
                  <Chip
                    label={STATUS_CONFIG[selectedReport.status]?.label || selectedReport.status}
                    color={STATUS_CONFIG[selectedReport.status]?.color || 'default'}
                    size="small"
                  />
                  {selectedReport.priority === 'school' && (
                    <Chip label="School Priority" color="error" size="small" variant="outlined" />
                  )}
                </Box>

                {/* Update Controls */}
                <FormControl fullWidth size="small">
                  <InputLabel>Update Status</InputLabel>
                  <Select
                    value={updateStatus}
                    label="Update Status"
                    onChange={e => setUpdateStatus(e.target.value)}
                  >
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="investigating">Investigating</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Resolution Notes"
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                  value={resolutionNotes}
                  onChange={e => setResolutionNotes(e.target.value)}
                  placeholder="Describe the fix or reason for rejection..."
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedReport(null)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleUpdateReport}
                disabled={saving}
              >
                {saving ? <CircularProgress size={20} /> : 'Update Report'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
